import { useFrame, useThree } from '@react-three/fiber';
import { create } from 'zustand';

/**
 * Plan 07-05 — dev-mode perf panel that extends the existing DebugHUD
 * (Plan 03-02) with calls / triangles / points / lines / geos / tex counters.
 *
 * Architecture: the perf panel is a HYBRID — sampling requires Canvas scope
 * (useFrame / useThree on gl.info.render), but the readout lives in DOM
 * alongside DebugHUD's other panels. We split the component:
 *
 *   1. <DebugHUDPerfProbe />  — Canvas-scope, mounted inside SceneGraph.
 *                               Samples gl.info every frame, writes into a
 *                               tiny Zustand slice. Gated on ?debug=1 so the
 *                               per-frame write is a no-op in production.
 *
 *   2. <DebugHUDPerfReadout /> — DOM, mounted inside DebugHUD.tsx alongside
 *                                the existing stats.js panel + navState readout.
 *                                Reads the slice and renders period-chrome pre block.
 *
 * No r3f-perf dep (07-RESEARCH.md Open Question 3 closure — in-repo extension
 * avoids ~30KB of bundle for a dev-only surface).
 */

interface PerfSlice {
  calls: number;
  triangles: number;
  points: number;
  lines: number;
  geometries: number;
  textures: number;
  setAll: (s: Omit<PerfSlice, 'setAll'>) => void;
}

/**
 * Internal slice — exported only for tests. In production DebugHUDPerfReadout
 * subscribes, DebugHUDPerfProbe writes on useFrame.
 */
export const usePerfStore = create<PerfSlice>((set) => ({
  calls: 0,
  triangles: 0,
  points: 0,
  lines: 0,
  geometries: 0,
  textures: 0,
  setAll: (s) => set(s),
}));

function isDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('debug') === '1';
}

/**
 * Canvas-scope probe — samples every frame when ?debug=1. Writes are cheap
 * (shallow set); Zustand's default equality skips re-renders when values
 * match what's already stored.
 */
export function DebugHUDPerfProbe() {
  const gl = useThree((s) => s.gl);
  const setAll = usePerfStore((s) => s.setAll);
  const enabled = isDebugEnabled();

  useFrame(() => {
    if (!enabled) return;
    const r = gl.info.render;
    const m = gl.info.memory;
    setAll({
      calls: r.calls,
      triangles: r.triangles,
      points: r.points,
      lines: r.lines,
      geometries: m.geometries,
      textures: m.textures,
    });
  });

  return null;
}

/**
 * DOM readout — monospace pre block in the period-chrome palette (cream / espresso
 * with warm-ochre border) matching DebugHUD's existing styling. Subscribes to
 * usePerfStore selector-by-selector so only the fields that change trigger re-render.
 */
export function DebugHUDPerfReadout() {
  const calls = usePerfStore((s) => s.calls);
  const triangles = usePerfStore((s) => s.triangles);
  const points = usePerfStore((s) => s.points);
  const lines = usePerfStore((s) => s.lines);
  const geometries = usePerfStore((s) => s.geometries);
  const textures = usePerfStore((s) => s.textures);

  if (!isDebugEnabled()) return null;

  return (
    <pre
      aria-hidden="true"
      data-testid="debug-hud-perf-readout"
      style={{
        fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
        fontSize: 11,
        lineHeight: 1.4,
        color: '#FDF6EE',
        background: 'rgba(59,47,47,0.85)',
        padding: '0.35rem 0.5rem',
        margin: '0.25rem 0 0 0',
        border: '1px solid #D4A87E',
        borderRadius: 4,
      }}
    >
      {`calls    ${String(calls).padStart(5)}
tri      ${String(triangles).padStart(6)}
points   ${String(points).padStart(5)}
lines    ${String(lines).padStart(5)}
geos     ${String(geometries).padStart(5)}
tex      ${String(textures).padStart(5)}`}
    </pre>
  );
}
